import type {
	CatalogCandidate,
	CatalogManualLink,
	CatalogMotornetAutoOutcome,
	CatalogProvider,
	CatalogRow,
	CatalogRowProvider,
	CatalogRowsRequest,
	CatalogUnavailableProvider,
	MotornetVehicleKind,
} from 'src/motornet/types';

export const PROVIDER_LABELS: Record<CatalogProvider, string> = {
	arval: 'Arval',
	ayvens: 'Ayvens',
	leasys: 'Leasys',
	alphabet: 'Alphabet',
};

const descending = (values: number[]) => Array.from(new Set(values)).sort((a, b) => b - a);

export const matchesListPrice = (price: number | null, vehiclePrice: number | null): boolean =>
	price != null && vehiclePrice != null && Math.abs(price - vehiclePrice) < 0.005;

export const PROVIDERS: CatalogProvider[] = ['arval', 'ayvens', 'leasys', 'alphabet'];

export type Chip = CatalogProvider | 'motornet';

export const CHIPS: Chip[] = [...PROVIDERS, 'motornet'];

export const CHIP_LABELS: Record<Chip, string> = { ...PROVIDER_LABELS, motornet: 'Motornet' };

export type ChipState = 'automatic' | 'manual' | 'missing' | 'unavailable';

const isUnavailable = (p: CatalogRowProvider | CatalogUnavailableProvider): p is CatalogUnavailableProvider =>
	p.unavailable === true;

export const providerListings = (row: CatalogRow): CatalogRowProvider[] =>
	row.providers.filter((p): p is CatalogRowProvider => !isUnavailable(p));

export const unavailableProvider = (row: CatalogRow, provider: CatalogProvider): CatalogUnavailableProvider | null =>
	row.providers.filter(isUnavailable).find((p) => p.provider === provider) ?? null;

const manualLink = (row: CatalogRow, provider: CatalogProvider, active: boolean): CatalogManualLink | null =>
	row.manualLinks.find((l) => l.provider === provider && l.action === 'link' && l.active === active) ?? null;

export const activeManualLink = (row: CatalogRow, provider: CatalogProvider) => manualLink(row, provider, true);

export const inactiveLinkReason = (row: CatalogRow, provider: CatalogProvider): string | null =>
	manualLink(row, provider, false)?.reason ?? null;

export const isManualLink = (row: CatalogRow, provider: CatalogProvider): boolean =>
	providerListings(row).find((p) => p.provider === provider)?.link === 'manual';

export const chipState = (row: CatalogRow, chip: Chip): ChipState => {
	if (chip === 'motornet') return row.motornet?.mode ?? 'missing';
	if (unavailableProvider(row, chip)) return 'unavailable';
	if (!providerListings(row).some((p) => p.provider === chip)) return 'missing';
	return isManualLink(row, chip) ? 'manual' : 'automatic';
};

export const rowYears = (row: CatalogRow): number[] =>
	row.modelYear != null
		? [row.modelYear]
		: descending(providerListings(row).map((p) => p.modelYear).filter((y): y is number => y != null));

export const manufacturerCodes = (row: CatalogRow): string[] =>
	Array.from(new Set((row.manufacturerCodes || []).filter((c): c is string => !!c)));

export const versionManufacturerCodes = (items: CatalogCandidate[], version: string): string[] =>
	Array.from(
		new Set(items.filter((c) => c.version === version).map((c) => c.manufacturerCode).filter((c): c is string => !!c))
	);

const normalizeName = (value: string) => value.trim().toUpperCase();

export const matchesManufacturerCode = (code: string | null | undefined, row: CatalogRow): boolean =>
	!!code &&
	providerListings(row).some((p) => !!p.manufacturerCode && normalizeName(p.manufacturerCode) === normalizeName(code));

export const findByName = <T>(
	items: T[],
	name: string | null | undefined,
	getName: (item: T) => string | null | undefined
): T | undefined => {
	if (!name) return undefined;
	return items.find((item) => {
		const itemName = getName(item);
		return !!itemName && normalizeName(itemName) === normalizeName(name);
	});
};

export const facetMatch = (values: string[], name: string | null | undefined) => findByName(values, name, (v) => v);

export const rowLabel = (r: { make: string | null; model: string | null; version: string | null }): string =>
	[r.make, r.model, r.version].filter(Boolean).join(' ');

export type CurrentRowWarning = 'none' | 'moves' | 'empties';

export const currentRowWarning = (candidate: CatalogCandidate, versionKey: string): CurrentRowWarning =>
	!candidate.currentRow || candidate.currentRow.versionKey === versionKey
		? 'none'
		: candidate.currentRow.providers.every((p) => p === candidate.provider)
		? 'empties'
		: 'moves';

export const motornetKind = (kind: string | null): MotornetVehicleKind | undefined =>
	kind === 'auto' || kind === 'vcom' ? kind : undefined;

export type CatalogFilterState = Omit<CatalogRowsRequest, 'search' | 'page' | 'pageSize'>;

export const EMPTY_FILTERS: CatalogFilterState = {};

export const activeFilterCount = (f: CatalogFilterState): number =>
	[
		f.vehicleKind,
		f.providers?.length ? f.providers : undefined,
		f.minMatches,
		f.state,
		f.hasStock,
		f.matrixAutoRenew,
		f.make,
		f.model,
	].filter((value) => value !== undefined).length;

export const outcomeLevel = (outcome: CatalogMotornetAutoOutcome): 'success' | 'warning' | 'error' =>
	outcome === 'associated' ? 'success' : outcome === 'lookup_failed' ? 'error' : 'warning';

export const findOption = <O extends { value: unknown }>(options: O[], value: O['value'] | undefined): O | null =>
	options.find((o) => o.value === value) ?? null;

export const selectedOption = (
	options: { value: string; label: string }[],
	value: string | undefined
): { value: string; label: string } | null =>
	value === undefined ? null : findOption(options, value) ?? { value, label: value };

export type ProviderView = 'pending' | 'wizard' | 'summary';

export const providerView = (state: ChipState, replacing: boolean, pending: boolean): ProviderView =>
	pending
		? 'pending'
		: state === 'missing' || state === 'unavailable' || (replacing && state === 'manual')
		? 'wizard'
		: 'summary';

export const MAX_MATCHES = 4;

export const parseMinMatches = (input: string): number | null => {
	const value = input.trim();
	if (!/^\d+$/.test(value)) return null;
	const count = Number(value);
	return count >= 1 && count <= MAX_MATCHES ? count : null;
};
